import React, { useCallback, useContext, useMemo } from 'react';
import { styled } from '@mui/material';
import { range } from 'ramda';
import { EDStickyTable } from '../../StickyTable/EDStickyTable';
import { EDStickyTableHeader } from '../../StickyTable/Components/EDStickyTableHeader';
import { EDStickyTableHeaderCell } from '../../StickyTable/Components/EDStickyTableHeaderCell';
import { EDStickyTableRows } from '../../StickyTable/Components/EDStickyTableRows';
import { EDStickyTableRow } from '../../StickyTable/Components/EDStickyTableRow';
import {
  EDStickyTableCell,
  EDStickyTableCellSelectedEvent,
  EDStickyTableCellSelectionClickEvent,
} from '../../StickyTable/Components/EDStickyTableCell';

import EDTypography from '../../Typography/EDTypography';
import {
  incDay,
  getDate,
  diffDay2,
  getWeekDayText,
  firstDayInMonth,
  firstDayInWeek,
  getDay,
  weekNo,
  getMonth,
  incMin,
} from '../../../Utils/dateUtils';
import {
  IBooking,
  ICalendarViewProps,
  prepareViewBookings,
  timeRangeOverlap,
  updateSelection,
} from '../Utils/EDCalendarViewUtils';
import EDBox from '../../EDBox/EDBox';
import { dateToStr } from '../../../Utils/stringUtils';
import {
  getDayNameShortText,
  getDayNameText,
  isRedDay,
} from '../../../Utils/dayNames';
import { firstUpper } from '../../../Utils/ramdaCookbook';
import { EDCalendarViewDayViewBookings } from './EDCalendarViewDayViewBookings';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';
import classNames from 'classnames';
import { ViewDbSettingsContext } from '../../../Features/Delsystem/Bokning/Rutiner/Kalender/Components/SchedulerComponent/ViewDbSettingsContext';

const DAY_HEAD_HEIGHT = 30;
const DAY_COLUMNS = 7;
const WEEK_ROWS = 6;
const MIN_DAY_WIDTH = 130;
const MIN_WEEK_HEIGHT = 40;
const SHORT_DAYNAME_LIMIT = 200;

export const EDCalendarViewMonthView: React.FC<ICalendarViewProps> = props => {
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);
  const tableHeight = Math.max(props.height - (props.bottomInsetPx ?? 0), 0);

  const viewStartTime = useMemo(
    () => firstDayInWeek(firstDayInMonth(props.currentDate)),
    [props.currentDate]
  );
  const viewEndTime = useMemo(
    () => incDay(viewStartTime, WEEK_ROWS * DAY_COLUMNS),
    [viewStartTime]
  );
  const dayWidth = props.width / DAY_COLUMNS;

  // Hantera bokningar
  const bookingDays = useMemo(
    () =>
      prepareViewBookings(
        props.bookings,
        viewStartTime,
        viewEndTime,
        booking => dateToStr(booking.startTime),
        false
      ),
    [props.bookings, viewStartTime, viewEndTime]
  );

  // Hantera markering
  const selStart = useMemo(() => props.selStart, [props.selStart]);
  const selEnd = useMemo(() => props.selEnd, [props.selEnd]);
  const selResources = useMemo(() => props.selResources, [props.selResources]);
  const allResIds = useMemo(
    () => props.resources.map(res => res.id),
    [props.resources]
  );

  const isSmallScreen = useIsSmallScreen();

  const onSelectionChange = props.onSelectionChange;
  const onSelectionClick = props.onSelectionClick;

  const handleCellSelected: EDStickyTableCellSelectedEvent = useCallback(
    (
      day: number,
      week: number,
      shift: boolean,
      _ctrl: boolean,
      drag: boolean
    ) => {
      const d = incDay(viewStartTime, week * 7 + day);
      if (onSelectionChange) {
        if ((shift || drag) && selStart && selEnd) {
          updateSelection(
            selStart,
            d,
            allResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        } else {
          updateSelection(
            d,
            d,
            allResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        }
      }
    },
    [
      viewStartTime,
      allResIds,
      selStart,
      selEnd,
      selResources,
      onSelectionChange,
    ]
  );

  const handleSelectionClick: EDStickyTableCellSelectionClickEvent =
    useCallback(
      ref => {
        if (onSelectionClick) {
          onSelectionClick(ref, selStart, selEnd, selResources);
        }
      },
      [selStart, selEnd, selResources, onSelectionClick]
    );

  return (
    <>
      <EDStickyTable
        width={props.width}
        height={tableHeight}
        colCount={DAY_COLUMNS}
        rowCount={WEEK_ROWS + 1}
        minColWidth={isSmallScreen ? 0 : MIN_DAY_WIDTH}
        minRowHeight={MIN_WEEK_HEIGHT}
        firstRowHeight={DAY_HEAD_HEIGHT}
        firstRowSticky={true}
        cellPadding={'1px 4px'}
      >
        <EDStickyTableHeader>
          {range(0, DAY_COLUMNS).map(day => {
            const redDay = day === 6;
            return (
              <EDStickyTableHeaderCell key={day}>
                <WeekDayText
                  className={classNames({
                    ['redDay']: redDay,
                    ['fontSizeMobile']: isSmallScreen,
                  })}
                >
                  {firstUpper(getWeekDayText(day, isSmallScreen))}
                </WeekDayText>
              </EDStickyTableHeaderCell>
            );
          })}
        </EDStickyTableHeader>
        <EDStickyTableRows>
          {range(0, WEEK_ROWS).map(week => (
            <EDStickyTableRow key={week}>
              {range(0, DAY_COLUMNS).map(day => {
                // Kalenderceller med bokningar
                const d = incDay(viewStartTime, week * 7 + day);
                const outside =
                  getMonth(d) !== getMonth(getDate(props.currentDate));
                const redDay = !outside && isRedDay(d);
                const today = diffDay2(d, getDate()) === 0;
                const dayName = firstUpper(
                  dayWidth < SHORT_DAYNAME_LIMIT
                    ? getDayNameShortText(d)
                    : getDayNameText(d)
                );
                const key = dateToStr(d);
                const bookings: IBooking[] = bookingDays[key];
                const cellStartTime = d;
                const cellEndTime = incDay(d, 1);
                const selected =
                  props.selStart &&
                  props.selEnd &&
                  timeRangeOverlap(
                    incMin(props.selStart, 15),
                    incMin(props.selEnd, 15),
                    cellStartTime,
                    cellEndTime
                  );
                return (
                  <EDStickyTableCell
                    key={day}
                    col={day}
                    row={week}
                    selected={selected}
                    onCellSelected={handleCellSelected}
                    onSelectionClick={handleSelectionClick}
                    today={today}
                    outside={outside}
                  >
                    <DayContainer>
                      <DayText
                        className={
                          redDay
                            ? 'redDay'
                            : outside
                            ? 'outside'
                            : today
                            ? 'today'
                            : undefined
                        }
                      >
                        <DayNumber
                          onClick={() => {
                            viewDbSettingsContext.setCurrentDate(d);
                            viewDbSettingsContext.setCurrentView('Day2');
                          }}
                          className={
                            isSmallScreen ? 'fontSizeMobile' : undefined
                          }
                        >
                          {getDay(d)}
                        </DayNumber>
                        {isSmallScreen ? null : dayName}
                        {day === 0 && (
                          <WeekNumber
                            className={classNames({
                              ['outside']: outside,
                              ['fontSizeMobile']: isSmallScreen,
                            })}
                          >
                            {`v.${weekNo(d)}`}
                          </WeekNumber>
                        )}
                      </DayText>
                    </DayContainer>
                    <EDCalendarViewDayViewBookings
                      bookings={bookings}
                      onBookingClick={props.onBookingClick}
                      view="All"
                    />
                  </EDStickyTableCell>
                );
              })}
            </EDStickyTableRow>
          ))}
        </EDStickyTableRows>
      </EDStickyTable>
    </>
  );
};

const WeekDayText = styled(EDTypography, { name: 'WeekDayText' })({
  fontSize: 18,
  '&.redDay': {
    color: 'red',
  },
  '&.fontSizeMobile': {
    fontSize: 12,
    fontWeight: 'bold',
  },
});

const DayContainer = styled(EDBox, { name: 'DayContainer' })({
  display: 'flex',
  flexDirection: 'column',
  height: '20px',
});

const DayText = styled(EDTypography, { name: 'DayText' })(() => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'relative',
  '&.outside': {
    color: 'gray',
  },
  '&.redDay': {
    color: 'red',
  },
  '&.today': {
    color: 'green',
    fontWeight: 'bold',
  },
}));

const DayNumber = styled('span', { name: 'DayNumber' })(({ theme }) => ({
  fontSize: 16,
  marginRight: theme.spacing(1),
  '&.fontSizeMobile': {
    fontSize: 12,
    fontWeight: 'bold',
  },
  cursor: 'pointer',
}));

const WeekNumber = styled(EDTypography, { name: 'WeekNumber' })(
  () => ({
    fontSize: 11,
    position: 'absolute',
    right: 4,
    top: 3,
    zIndex: 1,
    '&.outside': {
      color: 'gray',
    },
    '&.fontSizeMobile': {
      fontSize: 9,
    },
  })
);
